/**
 * "Drugs for separate administration" derivation.
 *
 * Source-PDF concept (USB LL0034-V07 §"ARZNEIMITTEL / INFUSIONEN ZUR
 * SEPARATEN VERABREICHUNG"): drugs that must be given through their own
 * line — they don't combine safely with others via Y-site. The matrix
 * encodes this as a blanket verdict against the `(any other drug)`
 * sentinel column: if that cell says `incompatible` or
 * `requires_line_flush`, the drug belongs in this group.
 *
 * Derivation is intentional — no separate `separate_admin: true` flag
 * on each drug. The blanket sentinel is the single source of truth;
 * adding a redundant flag would mean two places to update on every
 * matrix refresh, and they would silently drift the moment one is
 * forgotten.
 */

import type { CompatibilityBoardState, CompatibilityCell, CompatibilityDrug, VerdictId } from '@frontend/components/ysite/types'
import { getCell } from '@frontend/components/ysite/types'

/** Sentinel column id that carries the "vs. any other drug" blanket verdict. */
export const ANY_OTHER_DRUG_SENTINEL_ID = '(any other drug)'

/**
 * Verdicts against `(any other drug)` that classify a drug as
 * separate-admin. Both express "cannot share a line with arbitrary
 * other drugs": `incompatible` is the absolute block, `requires_line_flush`
 * is the must-flush-between variant — both end up as "give through its
 * own line in practice."
 */
const SEPARATE_ADMIN_VERDICTS = new Set<VerdictId>([
  'incompatible',
  'requires_line_flush',
])

/** True when this drug's `(any other drug)` blanket verdict marks it for separate admin. */
export function isSeparateAdmin(state: CompatibilityBoardState, drug: CompatibilityDrug): boolean {
  if (drug.sentinel) return false
  const blanket = getCell(state, ANY_OTHER_DRUG_SENTINEL_ID, drug.id)
  if (!blanket) return false
  return SEPARATE_ADMIN_VERDICTS.has(blanket.verdict)
}

export interface SeparateAdminEntry {
  drug: CompatibilityDrug
  /** The matrix cell that classified the drug — surfaces the verdict + any source citation. */
  cell: CompatibilityCell
}

/**
 * All drugs in the matrix flagged for separate administration, sorted
 * by display label so the list reads stably (alphabetical is what
 * nurses expect from a "you can't mix these" reference list).
 */
export function getSeparateAdminDrugs(state: CompatibilityBoardState): SeparateAdminEntry[] {
  const entries: SeparateAdminEntry[] = []
  for (const drug of state.drugs) {
    if (drug.sentinel) continue
    const blanket = getCell(state, ANY_OTHER_DRUG_SENTINEL_ID, drug.id)
    if (!blanket || !SEPARATE_ADMIN_VERDICTS.has(blanket.verdict)) continue
    entries.push({ drug, cell: blanket })
  }
  entries.sort((a, b) => a.drug.label.localeCompare(b.drug.label, undefined, { sensitivity: 'base' }))
  return entries
}
